import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  ShieldCheck, 
  Loader2, 
  Check, 
  ArrowLeft,
  FileText,
  Phone,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

// Volume-based pricing (must match landing page)
const calculatePrice = (numTrademarks) => {
  let perTM;
  let discount;
  if (numTrademarks >= 11) { perTM = 14; discount = 30; }
  else if (numTrademarks >= 6) { perTM = 16; discount = 20; }
  else if (numTrademarks >= 2) { perTM = 18; discount = 10; }
  else { perTM = 20; discount = 0; }
  return { perTM, discount, total: perTM * numTrademarks };
};

export const SignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingTrademarks, setPendingTrademarks] = useState([]);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  useEffect(() => {
    const session_id = searchParams.get('session_id');
    
    // Load pending trademarks from session storage
    const storedTrademarks = sessionStorage.getItem('pending_trademarks');
    if (storedTrademarks) {
      setPendingTrademarks(JSON.parse(storedTrademarks));
    }
    
    if (session_id) {
      verifyPayment(session_id);
    }
  }, [searchParams]);

  const verifyPayment = async (session_id) => {
    setIsProcessing(true);
    try {
      const response = await axios.get(`${API}/payments/status/${session_id}`);
      if (response.data.payment_status === 'paid') {
        setPaymentConfirmed(true);
        sessionStorage.removeItem('pending_trademarks');
        toast.success('Payment successful!');
      } else {
        toast.error('Payment not confirmed. Please try again.');
        navigate('/register-trademarks');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Error verifying payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      const originUrl = window.location.origin;
      const numTM = pendingTrademarks.length || 1;
      const pricing = calculatePrice(numTM);
      const submissionId = sessionStorage.getItem('submission_id') || '';
      
      const response = await axios.post(`${API}/payments/create-subscription`, {
        origin_url: originUrl,
        redirect_path: "/signup-success",
        email: pendingTrademarks[0]?.contact_email || "",
        num_trademarks: numTM,
        amount: pricing.total
      });

      if (response.data.url) {
        // Link payment session to submission
        if (submissionId && response.data.session_id) {
          await axios.post(`${API}/submissions/link-payment`, {
            submission_id: submissionId,
            payment_session_id: response.data.session_id
          }).catch(() => {}); // Don't block payment if linking fails
        }
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  // If processing payment verification
  if (isProcessing && searchParams.get('session_id')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900">Verifying your payment...</h2>
          <p className="text-slate-500 mt-2">Please wait a moment.</p>
        </div>
      </div>
    );
  }

  // Payment confirmed - Thank You page (replaces account creation)
  if (paymentConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg"
        >
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-8 text-center">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-teal-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Thank You for Your Payment</h1>
            <p className="text-slate-500 mb-6">
              Your payment has been received successfully. Our team will now begin setting up your trade mark monitoring.
            </p>

            {pendingTrademarks.length > 0 && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-teal-600" />
                  <span className="font-medium text-teal-700">
                    {pendingTrademarks.length} Trade Mark{pendingTrademarks.length > 1 ? 's' : ''} Submitted
                  </span>
                </div>
                <div className="space-y-1">
                  {pendingTrademarks.map((tm, i) => (
                    <p key={i} className="text-sm text-teal-600">• {tm.name}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-5 mb-6 text-left">
              <h3 className="font-bold text-slate-900 mb-3">What Happens Next?</h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <span>Our team will review your trade mark details and verify the information provided</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <span>We will configure your monitoring against newly filed UK trade mark applications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <span>You will receive a confirmation email with further details within 2 working days</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                  <span>Our expert team will notify you of any applications of interest requiring your attention</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-medium text-slate-900 mb-2">Questions?</h3>
              <div className="space-y-2 text-sm text-slate-600">
                <a href="tel:08000699090" className="flex items-center gap-2 hover:text-teal-600">
                  <Phone className="w-4 h-4" /> 0800 069 9090
                </a>
                <a href="mailto:trademarks@nbrg.co.uk" className="flex items-center gap-2 hover:text-teal-600">
                  <Mail className="w-4 h-4" /> trademarks@nbrg.co.uk
                </a>
              </div>
            </div>

            <Button 
              onClick={() => navigate('/landing')}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5"
              data-testid="signup-goto-home-btn"
            >
              Return to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Payment step - with volume-based pricing
  const numTM = pendingTrademarks.length || 1;
  const pricing = calculatePrice(numTM);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-8">
          <Link to="/register-trademarks" className="flex items-center gap-2 text-slate-500 hover:text-teal-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to trade mark details
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <img src={NBR_LOGO} alt="NBR" className="h-10 w-auto" />
            <div>
              <span className="font-heading font-black text-lg text-slate-900">NBR</span>
              <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex items-center gap-2 text-teal-600">
              <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-xs font-bold">
                <Check className="w-4 h-4" />
              </div>
              <span className="text-xs">Details</span>
            </div>
            <div className="w-8 h-0.5 bg-teal-600" />
            <div className="flex items-center gap-2 text-teal-600">
              <div className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <span className="text-xs font-medium">Payment</span>
            </div>
            <div className="w-8 h-0.5 bg-slate-300" />
            <div className="flex items-center gap-2 text-slate-400">
              <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs font-bold">
                3
              </div>
              <span className="text-xs">Complete</span>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">Complete Your Payment</h1>
          <p className="text-slate-500 mb-6">
            Secure payment to start your trade mark monitoring.
          </p>

          {/* Trademarks summary */}
          {pendingTrademarks.length > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-teal-600" />
                <span className="font-medium text-teal-700">
                  {pendingTrademarks.length} Trade Mark{pendingTrademarks.length > 1 ? 's' : ''} Ready
                </span>
              </div>
              <div className="space-y-1">
                {pendingTrademarks.map((tm, i) => (
                  <p key={i} className="text-sm text-teal-600">• {tm.name}</p>
                ))}
              </div>
            </div>
          )}

          {/* Volume-based pricing breakdown */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-1">
              <span className="font-medium text-slate-900">NBR Trade Mark Monitoring</span>
            </div>
            
            {/* Per trade mark price */}
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-slate-600">
                {numTM} Trade Mark{numTM > 1 ? 's' : ''} x £{pricing.perTM}/month
              </span>
              <span className="text-sm font-medium text-slate-700">
                £{pricing.total}/month
              </span>
            </div>
            
            {pricing.discount > 0 && (
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-teal-600">Volume discount applied ({pricing.discount}% off)</span>
                <span className="text-sm text-teal-600">
                  Save £{(20 * numTM) - pricing.total}/month
                </span>
              </div>
            )}

            <div className="border-t border-slate-200 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="font-bold text-slate-900">Total</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-slate-900">£{pricing.total}</span>
                  <span className="text-sm text-slate-500">/month</span>
                </div>
              </div>
              {pricing.discount > 0 && (
                <p className="text-xs text-slate-400 text-right mt-1">
                  Standard price: <span className="line-through">£{20 * numTM}/month</span>
                </p>
              )}
            </div>
            
            <div className="mt-4 space-y-2">
              {[
                "Professional monitoring service",
                "Pattern-based similarity detection",
                "Expert team review and reporting",
                "Opposition deadline tracking"
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          <Button 
            onClick={handlePayment}
            disabled={isProcessing}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6"
            data-testid="signup-pay-btn"
          >
            {isProcessing ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <ShieldCheck className="w-5 h-5 mr-2" />
            )}
            Pay £{pricing.total}/month
          </Button>

          <p className="text-center text-xs text-slate-400 mt-4">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SignUp;
